import * as React from 'react';
import {
    AppStoreAvailableBuild,
    CIBuildInfo,
    CommsMention,
    FeatureItem,
    IntegrationCapability,
    ReleaseCandidateBuildInfo,
} from '../../types';
import styles from '.././styles/stepStyles';
import { InfoCircleOutlined, FilterOutlined, ToolFilled, LinkOutlined, DatabaseFilled } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Popover from 'antd/lib/popover';
import Tooltip from 'antd/lib/tooltip';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import { FeatureSection, Nullable, ReleaseStepComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { compact } from 'lodash';
import { RootState } from '../../reducers/reducers';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectFeatureManagementIntegration,
    selectVCSIntegration,
    selectAllTrackingPropertiesForRelease,
    selectStabilityIntegration,
    selectShouldShowFixesOnly,
    selectIsReleaseKickedOff,
    selectSearch,
    selectIntegrationForCapability,
    selectReleaseStatus,
    selectShouldGroupWorkItemsByTicket,
    selectAutomationById,
    selectRequiresFixRequestsDetails,
    selectFixRequestsDetailsTemplate,
} from '../../selectors/selectors';
import {
    selectAppliedWorkItemFiltersCount,
    selectFilteredSortedPendingWorkItemsForStepRun,
    selectFilteredSortedDoneItemsForStepRun,
    selectAppliedFilters,
} from '../../selectors/memoizedSelectors';
import { PingAllPendingWorkOwnersRequest } from '../../actions/entities';
import Tabs from 'antd/lib/tabs';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import Spin from 'antd/lib/spin';
import FiltersComponent from './Filters';
import FeatureItemDrawer from './FeatureItemDrawer';
import AppliedFilters from './AppliedFilters';
import { FeatureReadinessItem } from './FeatureReadinessItem';
import SortComponent from './SortComponent';
import Space from 'antd/lib/space';
import Flex from 'antd/lib/flex';
import List from 'antd/lib/list';
import { AnyFilter, FilterType } from '../../types/filters';
import * as AnalyticEvents from '../../utils/analytics';
import { SortType } from '../../types/sorts';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import AddAFixModal from './AddAFixModal';
import { selectRequiresFixRequestsApprovals } from '../../selectors/selectors';
import Modal from 'antd/lib/modal';
import { PlusOutlined } from '@ant-design/icons';
import Tag from 'antd/lib/tag';
import Divider from 'antd/lib/divider';
import Dropdown from 'antd/lib/dropdown';
import {
    updateFeatureItemsFilters,
    updateFeatureItemsSearch,
    updateGroupWorkItemsByTicket,
    updateShowFixesOnly,
} from '../../actions/filters';
import { globalMessage } from '@/utils/globalMessage';
import { updateFeatureItemsSort } from '@/actions/sorts';
import Input from 'antd/lib/input';
import {
    getFeatureItemsFiltersStoredParams,
    getFeatureItemsGroupByTicketStoredParams,
    getFeatureItemsOnlyFixesStoredParams,
    getFeatureItemsSortStoredParams,
} from '@/helpers/localStorageHelper';
import { ROUTER_REDIRECT } from '@/helpers/routeHelpers';
import { useLocation } from 'react-router-dom';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import * as API from '@/api';
import { getFeatureItemsIgnoredCount, isReleaseComplete } from '@/helpers';
import { generateBackwardsCompatibleReleaseStepRunId } from '@/reducers/releaseStep';
import * as Log from '@/utils/logger';

const decodeSortFromURL = (currentSearch: string, shouldUseLocalStorage: boolean) => {
    const searchParams = new URLSearchParams(currentSearch);
    const sortType = shouldUseLocalStorage ? getFeatureItemsSortStoredParams() : searchParams.get('sort');

    return sortType;
};

const decodeShouldShowFixesOnlyFromURL = (currentSearch: string, shouldUseLocalStorage: boolean) => {
    const searchParams = new URLSearchParams(currentSearch);
    const showFixesOnly =
        shouldUseLocalStorage ? getFeatureItemsOnlyFixesStoredParams() : searchParams.get('showFixesOnly');

    return showFixesOnly === 'true';
};

const decodeShouldGroupWorkItemsByTicketFromURL = (currentSearch: string, shouldUseLocalStorage: boolean) => {
    const searchParams = new URLSearchParams(currentSearch);
    const groupItemsByTicket =
        shouldUseLocalStorage ? getFeatureItemsGroupByTicketStoredParams() : searchParams.get('groupWorkItemsByTicket');

    return groupItemsByTicket === 'true';
};

const encodeSearchQueryInURL = (currentSearch: string, query?: string) => {
    const searchParams = new URLSearchParams(currentSearch);

    if (query) {
        searchParams.set('query', query);
    } else {
        searchParams.delete('query');
    }

    return searchParams.toString();
};

const decodeSearchQueryFromURL = (currentSearch: string) => {
    const searchParams = new URLSearchParams(currentSearch);
    return searchParams.get('query');
};

const decodeFiltersFromURL = (currentSearch: string, shouldUseLocalStorage: boolean): AnyFilter[] => {
    const searchParams = new URLSearchParams(currentSearch);

    const filters = shouldUseLocalStorage ? getFeatureItemsFiltersStoredParams() : searchParams.get('filters');

    if (!filters) {
        return [];
    }

    try {
        return JSON.parse(filters);
    } catch {
        return []; // if we can't parse the filters, just clear them
    }
};

const decodeSelectedItemFromURL = (currentSearch: string) => {
    const searchParams = new URLSearchParams(currentSearch);
    const selectedItem = searchParams.get('selectedItem') ?? getFeatureItemsFiltersStoredParams();

    return selectedItem;
};

const encodeSelectedItemInURL = (selectedItemId: string, currentSearch: string) => {
    const searchParams = new URLSearchParams(currentSearch);

    if (selectedItemId) {
        searchParams.set('selectedItem', selectedItemId);
    } else {
        searchParams.delete('selectedItem');
    }

    return searchParams.toString();
};

const decodeSelectedFixFromURL = (currentSearch: string) => {
    const searchParams = new URLSearchParams(currentSearch);
    const selectedFix = searchParams.get('selectedFix') ?? getFeatureItemsFiltersStoredParams();

    return selectedFix;
};

const encodeSelectedFixInURL = (selectedFixId: string, currentSearch: string) => {
    const searchParams = new URLSearchParams(currentSearch);

    if (selectedFixId) {
        searchParams.set('selectedFix', selectedFixId);
    } else {
        searchParams.delete('selectedFix');
    }

    return searchParams.toString();
};

const encodeSelectedTabInURL = (selectedTab: string, currentSearch: string) => {
    const searchParams = new URLSearchParams(currentSearch);

    if (selectedTab) {
        searchParams.set('selectedTab', selectedTab);
    } else {
        searchParams.delete('selectedTab');
    }

    return searchParams.toString();
};

const decodeSelectedTabFromURL = (currentSearch: string) => {
    const searchParams = new URLSearchParams(currentSearch);
    const selectedTab = searchParams.get('selectedTab');

    return selectedTab;
};
const { Search } = Input;

interface FeatureReadinessItemsProps extends ReleaseStepComponentProps {
    workingBranchEnabled: boolean;
    userId: string;
    percentComplete: number;
    stepRunId: string;
    latestStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    latestWorkingBranchBuild: Nullable<CIBuildInfo>;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    autoUploadDsymsToStabilityMonitoringAutomationId: Nullable<string>;
    autoUploadBinaryArtifactToAppStoreAutomationId: Nullable<string>;
    autoDownloadArtifactsAutomationId: Nullable<string>;
    autoCherryPickAutomationId: Nullable<string>;
    autoMergeRunwayPRsAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

interface LocalLocationState {
    [ROUTER_REDIRECT]: boolean;
}

const FeatureReadinessItems: React.FC<FeatureReadinessItemsProps> = (props: FeatureReadinessItemsProps) => {
    const dispatch = useDispatch();
    const location = useLocation<LocalLocationState>();

    const [filtersVisible, setFiltersVisible] = React.useState(false);
    const [isPingPendingWorkOwnersLoading, setIsPingPendingWorkOwnersLoading] = React.useState(false);

    const [currentPageDoneItems, setCurrentPageDoneItems] = React.useState(1);
    const [currentPagePendingItems, setCurrentPagePendingItems] = React.useState(1);

    const backwardsCompatibleStepRunId = generateBackwardsCompatibleReleaseStepRunId(
        props.releaseId,
        props.releaseStepType,
    );

    const filteredSortedPendingItems = useSelector((state: RootState) =>
        selectFilteredSortedPendingWorkItemsForStepRun(state, backwardsCompatibleStepRunId, props.workingBranchEnabled),
    );

    const filteredSortedDoneItems = useSelector((state: RootState) =>
        selectFilteredSortedDoneItemsForStepRun(state, backwardsCompatibleStepRunId, props.workingBranchEnabled),
    );

    const featureManagementIntegration = useSelector((state: RootState) =>
        selectFeatureManagementIntegration(state, props.appId),
    );

    const onSearch = (query: string) => {
        dispatch(updateFeatureItemsSearch(query));
    };

    const { latestStagingBuild, selectedStagingBuild, latestWorkingBranchBuild } = props;

    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, props.appId));
    const stabilityIntegration = useSelector((state: RootState) => selectStabilityIntegration(state, props.appId));
    const commsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, props.appId, IntegrationCapability.Notifications),
    );

    const appliedFiltersCount = useSelector(selectAppliedWorkItemFiltersCount);
    const allReleaseTrackingProperties = useSelector((state: RootState) =>
        selectAllTrackingPropertiesForRelease(state, props.orgId, props.appId, props.releaseId),
    );

    const autoMergeRunwayPRs = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoMergeRunwayPRsAutomationId),
    );

    const isAutoMergeRunwayPRsEnabled = autoMergeRunwayPRs?.enabled;

    const isReleaseKickedOff = useSelector((state: RootState) => selectIsReleaseKickedOff(state, props.releaseId));
    const releaseStatus = useSelector((state: RootState) => selectReleaseStatus(state, props.releaseId));

    const [selectedFeatureItemId, setSelectedFeatureItemId] = React.useState(
        decodeSelectedItemFromURL(window.location.search) ?? null,
    );

    const [selectedFixRequestId, setSelectedFixRequestId] = React.useState(
        decodeSelectedFixFromURL(window.location.search) ?? null,
    );

    const [featureItemDrawerVisible, setFeatureItemDrawerVisible] = React.useState(false);

    const [showAddAFixModal, setShowAddAFixModal] = React.useState(false);
    const [workItemIdForFix, setWorkItemIdForFix] = React.useState(null);
    const showFixesOnly = useSelector((state: RootState) => selectShouldShowFixesOnly(state));
    const groupItemsByTicket = useSelector((state: RootState) => selectShouldGroupWorkItemsByTicket(state));
    const searchQuery = useSelector((state: RootState) => selectSearch(state));
    const [searchState, setSearchState] = React.useState(searchQuery);

    const requiresFixRequestsApprovals = useSelector(selectRequiresFixRequestsApprovals(props.appId));
    const requiresFixRequestsDetails = useSelector(selectRequiresFixRequestsDetails(props.appId));
    const fixRequestsDetailsTemplate = useSelector(selectFixRequestsDetailsTemplate(props.appId));

    const auth = useSelector((state: RootState) => state.auth);

    const allFeatureItems = [...filteredSortedPendingItems, ...filteredSortedDoneItems];

    React.useEffect(() => {
        const url = new URL(window.location.toString());
        url.search = encodeSearchQueryInURL(url.search, searchQuery);
        window.history.pushState({}, '', url);
    }, [searchQuery]);

    const isRouterLink = !!location?.state?.[ROUTER_REDIRECT];

    React.useEffect(() => {
        if (location) {
            const filtersFromURL = decodeFiltersFromURL(window.location.search, isRouterLink);
            const showFixesOnlyFromURL = decodeShouldShowFixesOnlyFromURL(window.location.search, isRouterLink);
            const groupByTicketFromURL = decodeShouldGroupWorkItemsByTicketFromURL(
                window.location.search,
                isRouterLink,
            );
            const sortTypeFromURL = decodeSortFromURL(window.location.search, isRouterLink) as SortType;
            const searchQuery = decodeSearchQueryFromURL(window.location.search);

            window.history.replaceState({}, '', window.location.pathname);
            dispatchUrlParams(
                dispatch,
                filtersFromURL,
                showFixesOnlyFromURL,
                groupByTicketFromURL,
                sortTypeFromURL,
                searchQuery,
                setSearchState,
            );
        }
    }, [location, isRouterLink]);

    const showDrawer = (record) => {
        return {
            onClick: () => {
                setFeatureItemDrawerVisible(true);
                setSelectedFeatureItemId(record.id);
                setSelectedFixRequestId(record.fixRequest?.id);
            },
        };
    };

    const pendingItemsTotalCount = filteredSortedPendingItems.length;
    const pendingItemsIgnoredCount = getFeatureItemsIgnoredCount(filteredSortedPendingItems);
    const pendingCountNotIgnored = pendingItemsTotalCount - pendingItemsIgnoredCount;

    const doneItemsTotalCount = filteredSortedDoneItems.length;
    const doneItemsIgnoredCount = getFeatureItemsIgnoredCount(filteredSortedDoneItems);
    const doneCountNotIgnored = doneItemsTotalCount - doneItemsIgnoredCount;

    const computeDoneAndPendingCounts = () => {
        const totalItemsCount = doneItemsTotalCount + pendingItemsTotalCount;

        const itemsCountNotIgnored = totalItemsCount - (pendingItemsIgnoredCount + doneItemsIgnoredCount);

        let pendingCountString = `${pendingCountNotIgnored} of ${itemsCountNotIgnored}`;
        let doneCountString = `${doneCountNotIgnored} of ${itemsCountNotIgnored}`;

        if (pendingItemsIgnoredCount > 0) {
            pendingCountString = pendingCountString + ` (${pendingItemsIgnoredCount} ignored)`;
        }

        if (doneItemsIgnoredCount > 0) {
            doneCountString = doneCountString + ` (${doneItemsIgnoredCount} ignored)`;
        }

        return {
            pendingCountString,
            doneCountString,
        };
    };

    const renderItem = (record: FeatureItem) => {
        if (!record) {
            Log.error(new Error(`Feature item is null for release ${props.releaseId}`));
            return null;
        }
        return (
            <List.Item
                style={{
                    padding: 0,
                    borderRadius: '8px',
                }}
            >
                <FeatureReadinessItem
                    orgId={props.orgId}
                    appId={props.appId}
                    releaseId={props.releaseId}
                    stepRunId={props.stepRunId}
                    isAutoMergeRunwayPRsEnabled={isAutoMergeRunwayPRsEnabled}
                    onClickRow={() => showDrawer(record).onClick()}
                    onClickAddFixRequest={(workItemId) => {
                        setWorkItemIdForFix(workItemId);
                        setShowAddAFixModal(true);
                    }}
                    workingBranchEnabled={props.workingBranchEnabled}
                    featureManagementIntegrationType={featureManagementIntegration?.id}
                    vcsIntegrationType={vcsIntegration?.id}
                    {...record}
                    latestOrSelectedStagingBuild={selectedStagingBuild ?? latestStagingBuild}
                    latestWorkingBranchBuild={latestWorkingBranchBuild}
                    requiresFixRequestsApprovals={requiresFixRequestsApprovals}
                    isReleaseKickedOff={isReleaseKickedOff}
                />
            </List.Item>
        );
    };

    const onApplyFilters = (filters: AnyFilter[]) => {
        setFiltersVisible(false);

        // track applied filters
        const appliedFilters = filters.map((filter) => {
            switch (filter.type) {
                // for some filters, it's useful to know their explicit value
                case FilterType.HideSubtasks:
                case FilterType.HideUnsquashedChildCommits:
                case FilterType.HideItemsWithMissingFAs:
                case FilterType.HideItemsWithTickets:
                case FilterType.TicketStatus:
                case FilterType.CodeStatus:
                case FilterType.ShowFixesOnly:
                    return filter;
                // for most, just the type of filter is fine
                default:
                    return {
                        type: filter.type,
                    };
            }
        }); // only care to see the filter type
        const allProperties = {
            ...allReleaseTrackingProperties,
            filters: appliedFilters,
        };

        AnalyticEvents.track(AnalyticEvents.Event.ApplyFilters, allProperties);
    };

    const onApplySort = (sortType: SortType) => {
        // track applied sort
        const allProperties = {
            ...allReleaseTrackingProperties,
            sortType,
        };

        AnalyticEvents.track(AnalyticEvents.Event.ApplySort, allProperties);
    };

    const filteredSortedPendingCount = filteredSortedPendingItems.length;
    const filteredSortedDoneCount = filteredSortedDoneItems.length;

    const { pendingCountString, doneCountString } = computeDoneAndPendingCounts();
    const featureCompleteAndHasDoneItems = props.percentComplete === 100 && filteredSortedDoneCount > 0;

    const [pingConfirmationModalVisible, setPingConfirmationModalVisible] = React.useState(false);
    const [pingAllOwners, setPingAllOwners] = React.useState(false);

    const disablePingPendingButton = pendingItemsTotalCount == 0;

    const getPingButtonText = (): string => {
        if (appliedFilters && Object.keys(appliedFilters).length > 0 && filteredSortedPendingItems.length > 0) {
            const ownerCount = usersToPing.length;
            return ownerCount === 1 ? 'Ping 1 pending owner' : `Ping ${ownerCount} pending owners`;
        }
        return 'Ping all pending owners';
    };

    const openPingItemOwnersModal = (pingAllOwners = false) => {
        setPingAllOwners(pingAllOwners);
        setPingConfirmationModalVisible(true);
    };

    const renderCTAButtons = () => {
        return (
            <Flex justify="center" align="center" style={{ marginTop: 20, width: '100%' }} gap="middle">
                <Button
                    onClick={() => {
                        setShowAddAFixModal(true);
                    }}
                    style={{ paddingLeft: 70, paddingRight: 70 }}
                    disabled={!isReleaseKickedOff || isReleaseComplete(releaseStatus)}
                >
                    <PlusOutlined style={{ color: runwayColors.primary }} />
                    {requiresFixRequestsApprovals ? 'Add a fix and request approval' : 'Add a fix'}
                </Button>
                {disablePingPendingButton ?
                    <Tooltip title="No pending items to ping">
                        <Dropdown.Button
                            menu={{ items: pingMenuItems, onClick: () => openPingItemOwnersModal(true) }}
                            onClick={() => openPingItemOwnersModal(false)}
                            style={{ maxWidth: 300 }}
                            disabled={true}
                            buttonsRender={([leftButton, rightButton]) => [
                                React.cloneElement(leftButton as React.ReactElement, {
                                    style: { width: 'inherit' },
                                }),
                                rightButton,
                            ]}
                        >
                            <IntegrationIcon integrationId={commsIntegration?.id} />
                            {getPingButtonText()}
                        </Dropdown.Button>
                    </Tooltip>
                :   <Dropdown.Button
                        menu={{ items: pingMenuItems, onClick: () => openPingItemOwnersModal(true) }}
                        onClick={() => openPingItemOwnersModal(false)}
                        style={{ maxWidth: 300 }}
                        loading={isPingPendingWorkOwnersLoading}
                        buttonsRender={([leftButton, rightButton]) => [
                            React.cloneElement(leftButton as React.ReactElement, {
                                style: { width: 'inherit' },
                            }),
                            rightButton,
                        ]}
                    >
                        <IntegrationIcon integrationId={commsIntegration?.id} />
                        {getPingButtonText()}
                    </Dropdown.Button>
                }
            </Flex>
        );
    };

    React.useEffect(() => {
        setCurrentPageDoneItems(1);
        setCurrentPagePendingItems(1);
    }, [props.workingBranchEnabled]);

    const logicalDefaultActiveKey =
        filteredSortedPendingCount > 0 ? 'pending'
        : featureCompleteAndHasDoneItems ? 'done'
        : 'pending';

    const defaultActiveKey = decodeSelectedTabFromURL(window.location.search) ?? logicalDefaultActiveKey;

    const { CheckableTag } = Tag;

    const appliedFilters = useSelector(selectAppliedFilters);
    const handleOnClickShowFixesOnly = (checked) => {
        const fixesType = FilterType.ShowFixesOnly as FilterType.ShowFixesOnly;
        const fixFilter = { type: fixesType, showFixesOnly: checked };

        const updatedFilters =
            appliedFilters.length > 0 ?
                appliedFilters.some((filter) => Object.values(filter).includes(fixesType)) ?
                    appliedFilters.map((filter) =>
                        filter.type === fixesType ?
                            checked ? fixFilter
                            :   null
                        :   filter,
                    )
                :   [...appliedFilters, fixFilter]
            : checked ? [fixFilter]
            : null;

        dispatch(updateFeatureItemsFilters({ filters: compact(updatedFilters) }));
        dispatch(updateShowFixesOnly(checked));
    };

    const handleOnClickGroupWorkItemsByTicket = (checked) => {
        dispatch(updateGroupWorkItemsByTicket(checked));
    };

    const selectedItem =
        selectedFixRequestId ?
            allFeatureItems
                ?.filter((item) => item.fixRequest?.id === selectedFixRequestId)
                .sort(
                    (a, b) => new Date(a.fixRequest.createdAt).getTime() - new Date(b.fixRequest.createdAt).getTime(),
                )?.[0]
        :   allFeatureItems?.find((item) => item.id === selectedFeatureItemId);
    React.useEffect(() => {
        if (selectedItem) {
            setFeatureItemDrawerVisible(true);
        }
    }, [selectedItem]);

    const authCredentials = auth.authCredentials;

    const [usersToPing, setUsersToPing] = React.useState<CommsMention[]>([]);
    const [isLoadingUsersToPing, setIsLoadingUsersToPing] = React.useState(false);

    const itemsToPingIds = React.useMemo(() => {
        if (appliedFilters && appliedFilters.length > 0 && filteredSortedPendingItems.length > 0) {
            return filteredSortedPendingItems.map((item) => item.id);
        }
        return undefined;
    }, [appliedFilters, filteredSortedPendingItems]);

    const itemsToPingKey = React.useMemo(() => (itemsToPingIds ? itemsToPingIds.join(',') : ''), [itemsToPingIds]);

    React.useEffect(() => {
        if (!pingConfirmationModalVisible) return;

        const request: PingAllPendingWorkOwnersRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            shouldPingAllWorkOwners: pingAllOwners,
        };

        if (itemsToPingIds && itemsToPingIds.length > 0) {
            request.itemsToPingOwnersOf = itemsToPingIds;
        }

        setIsLoadingUsersToPing(true);

        API.listPendingWorkOwnersComms(request, authCredentials)
            .then((mentions) => {
                setUsersToPing(mentions);
            })
            .catch((error) => {
                console.error('Error fetching users to ping:', error);
                setUsersToPing([]);
            })
            .finally(() => {
                setIsLoadingUsersToPing(false);
            });
    }, [pingConfirmationModalVisible, pingAllOwners, itemsToPingKey, props.orgId, props.appId, props.releaseId]);

    const pingWorkItemOwners = (pingAllOwners = false) => {
        AnalyticEvents.track(AnalyticEvents.Event.ClickPingPendingFROwners, allReleaseTrackingProperties);

        const baseRequest: PingAllPendingWorkOwnersRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            shouldPingAllWorkOwners: pingAllOwners,
        };

        let successMessage: string;

        if (pingAllOwners) {
            successMessage = 'All work item owners pinged!';
        } else if (appliedFilters && Object.keys(appliedFilters).length > 0 && filteredSortedPendingItems.length > 0) {
            baseRequest.itemsToPingOwnersOf = filteredSortedPendingItems.map((item) => item.id);
            successMessage = 'Filtered pending work item owners pinged!';
        } else {
            successMessage = 'Pending work item owners pinged!';
        }

        setIsPingPendingWorkOwnersLoading(true);

        API.pingAllPendingWorkOwners(baseRequest, authCredentials)
            .then(() => {
                globalMessage.success(successMessage);
                return;
            })
            .catch((e) => {
                globalMessage.error(e.message);
                return;
            })
            .finally(() => setIsPingPendingWorkOwnersLoading(false));
    };

    const pingMenuItems = [
        {
            key: 'ping-all',
            label: 'Ping all owners',
        },
    ];

    return (
        <>
            <EmptyStateWrapper
                releaseStepType={props.releaseStepType}
                appId={props.appId}
                releaseId={props.releaseId}
                featureSections={[FeatureSection.FeatureReadinessWorkItems]}
                shouldShowAsTooltip
            >
                <div>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', paddingRight: 12 }}>
                        <Search
                            placeholder="Search"
                            allowClear
                            onSearch={onSearch}
                            style={{ width: '22%' }}
                            value={searchState}
                            onChange={(value) => setSearchState(value.target.value)}
                        />
                    </div>
                    <Tabs
                        style={{ height: '100%', overflow: 'unset' }}
                        defaultActiveKey={defaultActiveKey}
                        tabBarStyle={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: runwayColors.white,
                            marginBottom: 0,
                        }}
                        onChange={(activeKey) => {
                            const url = new URL(window.location.toString());
                            url.search = encodeSelectedTabInURL(activeKey, url.search);
                            window.history.pushState({}, '', url);
                        }}
                        tabBarExtraContent={{
                            right: (
                                <div
                                    style={{
                                        padding: 12,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                    }}
                                >
                                    <Space>
                                        <Button
                                            icon={<LinkOutlined style={{ color: runwayColors.primary }} />}
                                            type="link"
                                            style={{ padding: 0, marginRight: 8 }}
                                            onClick={() => {
                                                navigator.clipboard.writeText(window.location.href);
                                                globalMessage.success('Link copied to clipboard!');
                                                AnalyticEvents.track(AnalyticEvents.Event.ClickCopyFRViewLink, {
                                                    ...allReleaseTrackingProperties,
                                                });
                                            }}
                                        >
                                            Copy link
                                        </Button>
                                        <CheckableTag
                                            checked={showFixesOnly}
                                            onChange={handleOnClickShowFixesOnly}
                                            style={{
                                                margin: 0,
                                                color: showFixesOnly ? runwayColors.white : runwayColors.black,
                                                borderColor: showFixesOnly ? runwayColors.primary : antGrey.grey5,
                                                borderStyle: 'solid',
                                                backgroundColor:
                                                    showFixesOnly ? runwayColors.primary : runwayColors.white,
                                            }}
                                        >
                                            <ToolFilled
                                                style={{
                                                    color: showFixesOnly ? runwayColors.white : antGrey.grey7,
                                                    marginRight: 5,
                                                }}
                                            />
                                            Show fixes only
                                        </CheckableTag>
                                        <CheckableTag
                                            checked={groupItemsByTicket}
                                            onChange={handleOnClickGroupWorkItemsByTicket}
                                            style={{
                                                margin: 0,
                                                color: groupItemsByTicket ? runwayColors.white : runwayColors.black,
                                                borderColor: groupItemsByTicket ? runwayColors.primary : antGrey.grey5,
                                                borderStyle: 'solid',
                                                backgroundColor:
                                                    groupItemsByTicket ? runwayColors.primary : runwayColors.white,
                                            }}
                                        >
                                            <DatabaseFilled
                                                style={{
                                                    color: groupItemsByTicket ? runwayColors.white : antGrey.grey7,
                                                    marginRight: 5,
                                                }}
                                            />
                                            Group by ticket
                                        </CheckableTag>
                                        <Divider type="vertical" style={{ height: 30 }}></Divider>
                                        <SortComponent onApplySort={onApplySort} />
                                        <Badge
                                            count={appliedFiltersCount > 0 ? appliedFiltersCount : null}
                                            style={{
                                                backgroundColor: runwayColors.primary,
                                                borderColor: runwayColors.primary,
                                                color: 'white',
                                                fontSize: 13,
                                            }}
                                        >
                                            <Popover
                                                overlayClassName="filters-overlay"
                                                destroyTooltipOnHide
                                                content={
                                                    <FiltersComponent
                                                        isAutoMergeRunwayPRsEnabled={isAutoMergeRunwayPRsEnabled}
                                                        releaseId={props.releaseId}
                                                        stepRunId={props.stepRunId}
                                                        appStoreSelectedBuilds={props.appStoreSelectedBuilds}
                                                        appId={props.appId}
                                                        isVisible={filtersVisible}
                                                        useWorkingBranch={props.workingBranchEnabled}
                                                        onApplyFilters={onApplyFilters}
                                                    />
                                                }
                                                title={
                                                    <Text
                                                        style={{
                                                            ...tagStyle,
                                                            fontSize: 12,
                                                            color: antGrey.grey7,
                                                            padding: 15,
                                                        }}
                                                    >
                                                        Filters
                                                    </Text>
                                                }
                                                trigger="click"
                                                open={filtersVisible}
                                                placement="left"
                                                onOpenChange={(open) => setFiltersVisible(open)}
                                            >
                                                <Button
                                                    icon={<FilterOutlined style={{ color: runwayColors.primary }} />}
                                                    type="default"
                                                >
                                                    Filter
                                                </Button>
                                            </Popover>
                                        </Badge>
                                    </Space>
                                </div>
                            ),
                        }}
                        className="fr-tabs"
                    >
                        <Tabs.TabPane
                            tab={
                                <div style={styles.featureReadinessSectionHeader}>
                                    <Text style={{ fontSize: 16 }}>Pending</Text>
                                    <Text style={{ marginLeft: 8, fontSize: 16 }} type="secondary">
                                        - {pendingCountString}
                                    </Text>
                                </div>
                            }
                            key="pending"
                            style={{ overflow: 'unset' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <AppliedFilters
                                    orgId={props.orgId}
                                    appId={props.appId}
                                    releaseId={props.releaseId}
                                    useWorkingBranch={props.workingBranchEnabled}
                                />
                            </div>
                            {renderCTAButtons()}
                            {filteredSortedPendingCount > 0 && (
                                <List
                                    pagination={{
                                        hideOnSinglePage: true,
                                        current: currentPagePendingItems,
                                        onChange: (page) => setCurrentPagePendingItems(page),
                                    }}
                                    renderItem={renderItem}
                                    itemLayout="horizontal"
                                    split={true}
                                    dataSource={filteredSortedPendingItems.map((item, index) => {
                                        return { ...item, key: index };
                                    })}
                                    className="fr-list"
                                />
                            )}
                            {filteredSortedPendingCount === 0 && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: 250,
                                    }}
                                >
                                    <Text type="secondary">No pending items.</Text>
                                </div>
                            )}
                        </Tabs.TabPane>
                        <Tabs.TabPane
                            tab={
                                <div style={styles.featureReadinessSectionHeader}>
                                    <Text style={{ fontSize: 16 }}>Done</Text>
                                    <Text style={{ marginLeft: 8, fontSize: 16 }} type="secondary">
                                        - {doneCountString}
                                    </Text>
                                    <Tooltip
                                        title="Items are considered Done when the ticket status matches one of your team's
        configured &ldquo;Done statuses&ldquo;, and any corresponding code has been merged."
                                        style={{ marginLeft: 8 }}
                                    >
                                        <InfoCircleOutlined style={{ color: runwayColors.info, marginLeft: 8 }} />
                                    </Tooltip>
                                </div>
                            }
                            style={{ overflow: 'unset' }}
                            key="done"
                        >
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <AppliedFilters
                                    orgId={props.orgId}
                                    appId={props.appId}
                                    releaseId={props.releaseId}
                                    useWorkingBranch={props.workingBranchEnabled}
                                />
                            </div>
                            {renderCTAButtons()}
                            {filteredSortedDoneCount > 0 && (
                                <List
                                    size="small"
                                    pagination={{
                                        hideOnSinglePage: true,
                                        current: currentPageDoneItems,
                                        onChange: (page) => setCurrentPageDoneItems(page),
                                    }}
                                    renderItem={renderItem}
                                    dataSource={filteredSortedDoneItems.map((item, index) => {
                                        return { ...item, key: index };
                                    })}
                                    className="fr-list"
                                    split={true}
                                />
                            )}
                            {filteredSortedDoneCount === 0 && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: 250,
                                    }}
                                >
                                    <Text type="secondary">No complete items.</Text>
                                </div>
                            )}
                        </Tabs.TabPane>
                    </Tabs>
                </div>
            </EmptyStateWrapper>
            <AddAFixModal
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId}
                stepRunId={props.stepRunId}
                isModalOpen={showAddAFixModal}
                onCancel={() => {
                    setShowAddAFixModal(false);
                    setWorkItemIdForFix(null);
                }}
                workingBranchEnabled={props.workingBranchEnabled}
                requiresFixRequestsApprovals={requiresFixRequestsApprovals}
                requiresFixRequestsDetails={requiresFixRequestsDetails}
                fixRequestsDetailsTemplate={fixRequestsDetailsTemplate}
                featureItemId={workItemIdForFix}
                autoCherryPickAutomationId={props.autoCherryPickAutomationId}
                autoMergeRunwayPRsAutomationId={props.autoMergeRunwayPRsAutomationId}
            />
            <FeatureItemDrawer
                appId={props.appId}
                releaseId={props.releaseId}
                stepRunId={props.stepRunId}
                orgId={props.orgId}
                isVisible={featureItemDrawerVisible}
                selectedItem={selectedItem}
                isAutoMergeRunwayPRsEnabled={isAutoMergeRunwayPRsEnabled}
                closeDrawer={() => {
                    setFeatureItemDrawerVisible(false);
                }}
                afterOpenChange={(open) => {
                    const url = new URL(window.location.toString());
                    url.search = encodeSelectedItemInURL(open ? selectedFeatureItemId : null, url.search);
                    url.search = encodeSelectedFixInURL(open ? selectedFixRequestId : null, url.search);
                    window.history.pushState({}, '', url);

                    if (!open) {
                        setSelectedFeatureItemId(null);
                    }
                }}
                vcsId={vcsIntegration?.id}
                isOnWorkingBranch={props.workingBranchEnabled}
                featureManagementId={featureManagementIntegration?.id}
                stabilityId={stabilityIntegration?.id}
                onApplyFilters={onApplyFilters}
                userId={props.userId}
                latestStagingBuild={props.latestStagingBuild}
                selectedStagingBuild={props.selectedStagingBuild}
                latestWorkingBranchBuild={props.latestWorkingBranchBuild}
                appStoreSelectedBuilds={props.appStoreSelectedBuilds}
                autoDownloadArtifactsAutomationId={props.autoDownloadArtifactsAutomationId}
                autoUploadBinaryArtifactToAppStoreAutomationId={props.autoUploadBinaryArtifactToAppStoreAutomationId}
                autoUploadDsymsToStabilityMonitoringAutomationId={
                    props.autoUploadDsymsToStabilityMonitoringAutomationId
                }
                stepRunComponentProps={props.stepRunComponentProps}
            />
            <Modal
                title="Ping work item owners"
                open={pingConfirmationModalVisible}
                onCancel={() => setPingConfirmationModalVisible(false)}
                onOk={() => {
                    pingWorkItemOwners(pingAllOwners);
                    setPingConfirmationModalVisible(false);
                }}
                okText={`Ping ${usersToPing.length} owner${usersToPing.length === 1 ? '' : 's'}`}
                cancelText="Cancel"
                okButtonProps={{
                    type: 'primary',
                    disabled: isLoadingUsersToPing || usersToPing.length === 0,
                }}
                destroyOnClose
            >
                <div>
                    {isLoadingUsersToPing ?
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                            <Spin />
                        </div>
                    : usersToPing.length > 0 ?
                        <>
                            <Text>The following users will be pinged:</Text>
                            <div style={{ marginTop: 12, maxHeight: 200, overflowY: 'auto' }}>
                                <Text>
                                    {usersToPing.map((mention, index) => (
                                        <span key={mention.email}>
                                            <Text code>@{mention.displayName || mention.email}</Text>
                                            {index < usersToPing.length - 1 && ', '}
                                        </span>
                                    ))}
                                </Text>
                            </div>
                        </>
                    :   <Text>No users to ping</Text>}
                </div>
            </Modal>
        </>
    );
};

export default FeatureReadinessItems;

function dispatchUrlParams(
    dispatch,
    filtersFromURL: AnyFilter[],
    showFixesOnlyFromURL: boolean,
    groupWorkItemsByTicket: boolean,
    sortTypeFromURL: SortType,
    searchQuery?: string,
    setSearchState?: React.Dispatch<React.SetStateAction<string>>,
) {
    dispatch(updateFeatureItemsFilters({ filters: filtersFromURL }));
    dispatch(updateShowFixesOnly(showFixesOnlyFromURL));
    dispatch(updateGroupWorkItemsByTicket(groupWorkItemsByTicket));
    if (sortTypeFromURL) {
        dispatch(updateFeatureItemsSort({ sortType: sortTypeFromURL }));
    }
    if (searchQuery) {
        setSearchState?.(searchQuery);
        dispatch(updateFeatureItemsSearch(searchQuery));
    }
}
